/*
 * company-carousel.js — <company-carousel> (sections/company-carousel.liquid).
 *
 * Steps the press logos: every INTERVAL the active logo leaves while the
 * next one comes in. Which way they move (up on desktop, left on phones, a
 * crossfade under reduced motion) lives in the CSS; this only swaps the
 * classes. It runs while the strip is on screen and the tab is visible.
 *
 * First load: the logos are lazy images, so they used to draw in piece by
 * piece as they downloaded, which read as a fade before the slides started.
 * They're now fetched a screen ahead of the strip, and until all of them
 * have decoded the CSS parks the first logo below the slot; data-ready
 * then slides it in like the rest.
 */

if (!customElements.get('company-carousel')) {
  customElements.define(
    'company-carousel',
    class CompanyCarousel extends HTMLElement {
      static SLIDE = 600; // matches the CSS transition
      static INTERVAL = 3600; // 3s on show + the slide

      connectedCallback() {
        this.logos = Array.from(this.querySelectorAll('.company-carousel__logo'));
        if (!this.logos.length) return;
        this.images = Array.from(this.querySelectorAll('img'));
        this.index = Math.max(
          0,
          this.logos.findIndex((logo) => logo.classList.contains('is-active'))
        );
        this.onScreen = false;
        this.sync = this.sync.bind(this);

        // Already showing with every image in (a short page, or from cache):
        // skip the entrance rather than hide a logo that's on screen.
        const rect = this.getBoundingClientRect();
        const inView = rect.top < window.innerHeight && rect.bottom > 0;
        if (inView && this.images.every((img) => img.complete && img.naturalWidth)) {
          this.dataset.ready = '';
        }

        this.nearObserver = new IntersectionObserver(
          ([entry]) => {
            if (!entry.isIntersecting) return;
            this.nearObserver.disconnect();
            this.load();
          },
          { rootMargin: '100% 0px' }
        );
        this.nearObserver.observe(this);

        this.observer = new IntersectionObserver(
          ([entry]) => {
            this.onScreen = entry.intersectionRatio >= 0.3;
            this.sync();
          },
          { threshold: [0, 0.3] }
        );
        this.observer.observe(this);
        document.addEventListener('visibilitychange', this.sync);
      }

      disconnectedCallback() {
        this.nearObserver?.disconnect();
        this.observer?.disconnect();
        document.removeEventListener('visibilitychange', this.sync);
        this.stop();
      }

      // Fetch every logo now and resolve once they've all decoded (a broken
      // image doesn't hold the strip back).
      load() {
        if (!this.loading) {
          this.images.forEach((img) => (img.loading = 'eager'));
          this.loading = Promise.all(this.images.map((img) => img.decode().catch(() => {})));
        }
        return this.loading;
      }

      sync() {
        if (!this.onScreen || document.hidden) return this.stop();
        if (this.hasAttribute('data-ready')) return this.start();
        this.load().then(() => {
          this.dataset.ready = '';
          this.sync();
        });
      }

      start() {
        if (this.logos.length < 2 || this.timer) return;
        this.timer = setInterval(() => this.advance(), CompanyCarousel.INTERVAL);
      }

      stop() {
        clearInterval(this.timer);
        this.timer = null;
      }

      advance() {
        const leaving = this.logos[this.index];
        this.index = (this.index + 1) % this.logos.length;
        leaving.classList.replace('is-active', 'is-leaving');
        this.logos[this.index].classList.add('is-active');
        // back to the waiting position once it's out of sight
        setTimeout(() => leaving.classList.remove('is-leaving'), CompanyCarousel.SLIDE);
      }
    }
  );
}
